import React from "react";
import { Users, BookOpen, ShieldCheck, MessageCircle, LifeBuoy } from "lucide-react";
import { motion } from "framer-motion";

const helpTopics = [
  {
    title: "Find Study Partners",
    description:
      "Connect with focused aspirants preparing for competitive exams.",
    icon: <Users className="w-7 h-7 text-[#3498db]" />,
  },
  {
    title: "Collaboration Guide",
    description:
      "Understand how group study, discussions, and collaboration improve learning outcomes.",
    icon: <MessageCircle className="w-7 h-7 text-[#3498db]" />,
  },
  {
    title: "Privacy & Data Safety",
    description:
      "Know how your personal information and study data are protected on Study Partners.",
    icon: <ShieldCheck className="w-7 h-7 text-[#3498db]" />,
  },
  {
    title: "Contact Support",
    description:
      "Need help? Our team is ready to assist you.",
    icon: <LifeBuoy className="w-7 h-7 text-[#3498db]" />,
  },
];

const Help = () => {
  return (
    <section className="relative min-h-screen bg-[#C8F1F9] py-24 px-6 lg:px-24 overflow-hidden">
      
      {/* Decorative Blurs */}
      <div className="absolute top-24 left-12 w-72 h-72 bg-[#3498db]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-24 right-12 w-72 h-72 bg-pink-300/20 rounded-full blur-3xl" />

      {/* Heading */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="relative text-center mb-16"
      >
        <h2 className="text-4xl md:text-5xl font-extrabold text-[#3498db] mb-4">
          Help & Support
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Everything you need to navigate, collaborate, and prepare confidently.
        </p>
      </motion.div>

      {/* Cards */}
      <div className="relative max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-8">
        {helpTopics.map((topic, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="
              group relative
              bg-white/80 backdrop-blur-xl
              rounded-3xl
              border border-white/40
              shadow-[0_20px_60px_rgba(0,0,0,0.12)]
              hover:shadow-[0_30px_90px_rgba(0,0,0,0.18)]
              transition-all duration-500
              p-7 flex gap-5
            "
          >
           <motion.div
  initial={{ scale: 0.8, opacity: 0 }}
  whileInView={{ scale: 1, opacity: 1 }}
  animate={{
    y: [0, -8, 0],
    rotate: [0, 4, -4, 0],
  }}
  transition={{
    duration: 3,
    ease: "easeInOut",
    repeat: Infinity,
  }}
  whileHover={{
    scale: 1.2,
    rotate: 10,
  }}
  className="
    flex items-center justify-center
    w-16 h-16 rounded-2xl
    bg-transparent
  "
>
  {topic.icon}
</motion.div>

            {/* Content */}
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#3498db] transition">
                {topic.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                {topic.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Help;
